import React, { useEffect, useState } from 'react';
import { Icon, Input, Popconfirm, Select, Tag, Tooltip, message } from 'antd';
import { PurpleTooltip } from './businessLogicTable';
import { ObjectNameChip, Placeholder, TableLogicalOperator, TableOperator, StyledSelect } from './styles';
import { BUSINESS_LOGIC_TAB } from '../translationValues';
import { OPERATOR_VALUES } from '.';
import { minusIcon } from '../../../images/svgIcons';

const Option = Select.Option;

const updateTableColumnHeight = () => {
  const trs = document.querySelectorAll('div.table-container > table > tbody > tr');
  trs &&
    trs.forEach(tr => {
      const { firstChild, clientHeight } = tr;
      firstChild.style.height = clientHeight + 'px';
    });
};

const TableTags = ({ rowItem, extraItem, updateOperatorByKey }) => {
  const item = extraItem || rowItem;
  const tags = item.value ? item.value.split(',') : [];
  const handleClose = tagIndex => {
    const newTags = tags.filter((item, index) => index !== tagIndex).join(',');
    updateOperatorByKey(newTags, rowItem, 'value', extraItem);
  };
  return tags.map((tag, index) => (
    <Tag closable onClose={() => handleClose(index)} key={tag + index}>
      {tag}
    </Tag>
  ));
};

export const TableMainBody = ({ tableBodyData = [], globalVariables, isNewFlow, t, ...props }) => {
  const {
    updateVariableWithKey,
    updateOperatorByKey,
    setRuleDeleteIndex,
    flowHints,
    updateFlowHints,
    addExtraCondition,
    deleteExtraCondition,
    deleteCondition,
  } = props;

  const [variableHintProps, setVariableHintProps] = useState({});

  useEffect(() => updateTableColumnHeight(), [tableBodyData]);

  useEffect(() => {
    let showTimeout;
    if (isNewFlow) {
      showTimeout = setTimeout(() => setVariableHintProps({ visible: true }), 3500);
    }
    return () => {
      clearTimeout(showTimeout);
    };
  }, []);

  const variablePlaceholderClick = (value, variableItem, key) => {
    updateVariableWithKey(value, variableItem, key);
    const timeout = setTimeout(() => {
      const variableNode = document.getElementById(`${variableItem.variableId}-rule-condition-select`);
      variableNode && variableNode.click();
      clearTimeout(timeout);
    }, 100);
  };

  const updateObjectName = (objectName, rowItem) => {
    updateVariableWithKey(objectName, rowItem, 'objectName');
    if (rowItem.rules && rowItem.rules.length > 0) {
      rowItem.rules.forEach(rule => {
        updateOperatorByKey(objectName, rule, 'objectName');
        if (rule.extraConditions && rule.extraConditions.length > 0) {
          rule.extraConditions.forEach(extraCondition => {
            updateOperatorByKey(objectName, rule, 'objectName', extraCondition);
          });
        }
      });
    }
  };

  const handleVariableSelect = (value, rowItem, bodyIndex) => {
    const { key } = value;
    if (typeof key === 'string' && (key?.startsWith('object:') || key?.startsWith('groupObject:'))) {
      const name = key.split(':').slice(1).join(':');
      updateObjectName(name, rowItem);
      return;
    }
    const varType = globalVariables.find(v => v.id === key)?.type?.toLowerCase();
    updateVariableWithKey(varType, rowItem, 'type');
    updateVariableWithKey(key, rowItem, 'variableId');
    if (isNewFlow) {
      updateFlowHints({ ...flowHints, variableHintDone: true });
      updateOperatorByKey(false, tableBodyData[0][1], 'isNew');
      const hideTimeout = setTimeout(() => {
        setVariableHintProps({ visible: false });
        setVariableHintProps({});
        clearTimeout(hideTimeout);
      }, 1);
    }
    tableBodyData[bodyIndex].forEach((item, index) =>
      index !== 0 ? updateOperatorByKey(key, item, 'variableId') : null,
    );
  };

  const onMouseHoverRow = event => {
    const { clientHeight, lastChild } = event.currentTarget;
    // height of the parent tr divided by 2 and subtract 12 pixels
    // which is the radius of the subtract icon to position the element at center.
    lastChild.style.marginTop = clientHeight / 2 - 12 + 'px';
  };

  //handle enter or blur  for 'equals any of' input.
  const handleEnterOrBlur = (event, rowItem, extraItem) => {
    if ((event.which === 13 || event.type === 'blur') && event.target.value) {
      const item = extraItem || rowItem;
      const isTagAlreadyExists = item.value.split(',').includes(event.target.value);
      if (!isTagAlreadyExists) {
        const previousValue = item.value ? `${item.value},` : '';
        const value = previousValue + event.target.value;
        updateOperatorByKey(value, rowItem, 'value', extraItem);
        event.target.value = '';
      } else {
        message.error(t(
          BUSINESS_LOGIC_TAB.UNIQUE_VALUE_ERROR.key,
          BUSINESS_LOGIC_TAB.UNIQUE_VALUE_ERROR.value,
        ));
      }
    }
  };

  return tableBodyData.map((bodyItem, bodyIndex) => {
    return (
      <tr onMouseEnter={onMouseHoverRow}>
        {bodyItem.map((rowItem, rowIndex) => {
          if (rowIndex === 0) {
            const { variableId, isNew, objectName } = rowItem;
            const name = globalVariables?.find(gVar => gVar.id === variableId)?.placeHolderText;
            return (
              <td>
                <div className="condition" style={{ display: 'flex', alignItems: 'center' }}>
                  {objectName && (
                    <ObjectNameChip>
                      <Tooltip title={objectName}>
                        <span className="object-name-label">{objectName}</span>
                      </Tooltip>
                      <span
                        className="object-name-clear"
                        onClick={() => updateObjectName('', rowItem)}
                      >
                        <Icon type="close" />
                      </span>
                    </ObjectNameChip>
                  )}
                  <PurpleTooltip
                    overlay={t(
                      BUSINESS_LOGIC_TAB.CONDITION_SELECT_TOOLTIP_TEXT.key,
                      BUSINESS_LOGIC_TAB.CONDITION_SELECT_TOOLTIP_TEXT.value,
                    )}
                    placement="top"
                    visible={bodyIndex === 0 && variableHintProps?.visible}
                  >
                    {!isNew ? (
                      <StyledSelect
                        value={name ? { key: variableId, label: name } : undefined}
                        id={`${variableId}-rule-condition-select`}
                        showSearch={true}
                        showArrow={false}
                        dropdownMatchSelectWidth={false}
                        placeholder={t(
                          BUSINESS_LOGIC_TAB.BL_VARIABLE_PLACEHOLDER.key,
                          BUSINESS_LOGIC_TAB.BL_VARIABLE_PLACEHOLDER.value,
                        )}
                        labelInValue
                        style={{ minWidth: !name ? '150px' : undefined }}
                        onChange={value => handleVariableSelect(value, rowItem, bodyIndex)}
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        {globalVariables.map(gVariable => (
                          (gVariable.type === 'object' || gVariable.type === 'groupObject') ? (
                            <Option key={`${gVariable.type}:${gVariable.name}`} value={`${gVariable.type}:${gVariable.name}`}>
                              {gVariable.name}
                            </Option>
                          ) : (
                            <Option key={gVariable.id} value={gVariable.id}>{gVariable.placeHolderText}</Option>
                          )
                        ))}
                      </StyledSelect>
                    ) : (
                      <Placeholder
                        onClick={() => variablePlaceholderClick(false, rowItem, 'isNew')}
                      >
                        {isNewFlow && !flowHints.variableHintDone ? (
                          t(
                            BUSINESS_LOGIC_TAB.BL_VARIABLE_TEXT.key,
                            BUSINESS_LOGIC_TAB.BL_VARIABLE_TEXT.value,
                          )
                        ) : (
                          <Icon type="plus" />
                        )}
                      </Placeholder>
                    )}
                  </PurpleTooltip>
                </div>
              </td>
            );
          }

          const { isNew, operator, value, extraConditions } = rowItem;
          if (isNew) {
            if (isNewFlow && !flowHints.variableHintDone) {
              return (
                <td
                  onMouseEnter={() => setRuleDeleteIndex(rowIndex)}
                  onMouseLeave={() => setRuleDeleteIndex(0)}
                ></td>
              );
            }
          }

          return (
            <td
              onMouseEnter={() => setRuleDeleteIndex(rowIndex)}
              onMouseLeave={() => setRuleDeleteIndex(0)}
            >
              <TableOperator>
                <Select
                  value={operator}
                  onChange={value => updateOperatorByKey(value, rowItem, 'operator')}
                  placeholder={t(
                    BUSINESS_LOGIC_TAB.OPERATOR_PLACEHOLDER.key,
                    BUSINESS_LOGIC_TAB.OPERATOR_PLACEHOLDER.value,
                  )}
                  dropdownMatchSelectWidth={false}
                  suffixIcon={<Icon type="caret-down" />}
                  getPopupContainer={triggerNode => triggerNode.parentNode}
                >
                  {Object.keys(OPERATOR_VALUES).map(key => (
                    <Option value={OPERATOR_VALUES[key]}>{OPERATOR_VALUES[key]}</Option>
                  ))}
                </Select>
                {operator !== OPERATOR_VALUES.EQUALS_ANY_OF ? (
                  operator !== OPERATOR_VALUES.IS_EMPTY ?
                  <Input
                    value={value}
                    onChange={event => updateOperatorByKey(event.target.value, rowItem, 'value')}
                    placeholder={t(
                      BUSINESS_LOGIC_TAB.VALUE_LABEL.key,
                      BUSINESS_LOGIC_TAB.VALUE_LABEL.value,
                    )}
                  />
                  : <Input value={''} disabled />
                ) : (
                  <input
                    id="tag-input"
                    onKeyPress={event => handleEnterOrBlur(event, rowItem)}
                    onBlur={event => handleEnterOrBlur(event, rowItem)}
                    placeholder={t(
                      BUSINESS_LOGIC_TAB.VALUE_LABEL.key,
                      BUSINESS_LOGIC_TAB.VALUE_LABEL.value,
                    )}
                  />
                )}
              </TableOperator>
              {operator === OPERATOR_VALUES.EQUALS_ANY_OF ? (
                <TableTags rowItem={rowItem} updateOperatorByKey={updateOperatorByKey} />
              ) : (
                <></>
              )}
              {extraConditions.map(extraItem => {
                const { operator: extraOperator, value: extraValue } = extraItem;
                return (
                  <>
                    <TableLogicalOperator>---or---</TableLogicalOperator>
                    <TableOperator>
                      <Select
                        value={extraOperator}
                        onChange={value =>
                          updateOperatorByKey(value, rowItem, 'operator', extraItem)
                        }
                        placeholder={t(
                          BUSINESS_LOGIC_TAB.OPERATOR_PLACEHOLDER.key,
                          BUSINESS_LOGIC_TAB.OPERATOR_PLACEHOLDER.value,
                        )}
                        dropdownMatchSelectWidth={false}
                        suffixIcon={<Icon type="caret-down" />}
                      >
                        {Object.keys(OPERATOR_VALUES).map(key => (
                          <Option value={OPERATOR_VALUES[key]}>{OPERATOR_VALUES[key]}</Option>
                        ))}
                      </Select>
                      {extraOperator !== OPERATOR_VALUES.EQUALS_ANY_OF ? (
                        extraOperator !== OPERATOR_VALUES.IS_EMPTY ?
                          <Input
                            value={extraValue}
                            onChange={event =>
                              updateOperatorByKey(event.target.value, rowItem, 'value', extraItem)
                            }
                            placeholder={t(
                              BUSINESS_LOGIC_TAB.VALUE_LABEL.key,
                              BUSINESS_LOGIC_TAB.VALUE_LABEL.value,
                            )}
                          />
                        : <Input value={''} disabled />
                      ) : (
                        <input
                          id="tag-input"
                          onKeyPress={event => handleEnterOrBlur(event, rowItem, extraItem)}
                          onBlur={event => handleEnterOrBlur(event, rowItem, extraItem)}
                          placeholder={t(
                            BUSINESS_LOGIC_TAB.VALUE_LABEL.key,
                            BUSINESS_LOGIC_TAB.VALUE_LABEL.value,
                          )}
                        />
                      )}
                      <Popconfirm
                        title={t(
                          BUSINESS_LOGIC_TAB.DELETE_BL_CONFIRM.key,
                          BUSINESS_LOGIC_TAB.DELETE_BL_CONFIRM.value,
                        )}
                        okText={t(
                          BUSINESS_LOGIC_TAB.BL_CONFIRM_YES.key,
                          BUSINESS_LOGIC_TAB.BL_CONFIRM_YES.value,
                        )}
                        cancelText={t(
                          BUSINESS_LOGIC_TAB.BL_CONFIRM_NO.key,
                          BUSINESS_LOGIC_TAB.BL_CONFIRM_NO.value,
                        )}
                        onConfirm={() => deleteExtraCondition(rowItem, extraItem)}
                      >
                        <Tooltip
                          title={t(
                            BUSINESS_LOGIC_TAB.DELETE_CONDITION.key,
                            BUSINESS_LOGIC_TAB.DELETE_CONDITION.value,
                          )}
                        >
                          <Icon type="delete" style={{ alignSelf: 'center' }} />
                        </Tooltip>
                      </Popconfirm>
                    </TableOperator>
                    {extraOperator === OPERATOR_VALUES.EQUALS_ANY_OF ? (
                      <TableTags
                        rowItem={rowItem}
                        extraItem={extraItem}
                        updateOperatorByKey={updateOperatorByKey}
                      />
                    ) : (
                      <></>
                    )}
                  </>
                );
              })}
              <div>
                <span
                  onClick={() => addExtraCondition(rowItem)}
                  style={{ fontSize: '12px', cursor: 'pointer' }}
                >
                  <Icon type="plus" />{' '}
                  {t(BUSINESS_LOGIC_TAB.ADD_MORE.key, BUSINESS_LOGIC_TAB.ADD_MORE.value)}
                </span>
              </div>
            </td>
          );
        })}
        {tableBodyData.length > 1 ? (
          <PurpleTooltip
            title={t(
              BUSINESS_LOGIC_TAB.REMOVE_CONDITION_TOOLTIP.key,
              BUSINESS_LOGIC_TAB.REMOVE_CONDITION_TOOLTIP.value,
            )}
          >
            <div>
              <Icon
                onClick={() => deleteCondition(bodyIndex, tableBodyData[0][1])}
                component={minusIcon}
              />
            </div>
          </PurpleTooltip>
        ) : (
          <></>
        )}
      </tr>
    );
  });
};

export const TableDisplayBody = ({ tableBodyDisplay = [], updateRuleByKey, ...props }) => {
  const {
    flowHints,
    updateFlowHints,
    setRuleDeleteIndex,
    allBlocks = [],
    diffBlocks = [],
    t,
  } = props;

  const sectionPlaceholderClick = (value, rule, key) => {
    updateRuleByKey(value, rule, key);
    updateFlowHints({ ...flowHints, blockHintDone: true });
    setTimeout(() => document.getElementById(`${rule.id}-rule-section-name-select`).click(), 100);
  };

  return (
    <tr>
      {tableBodyDisplay.map((item, index) => {
        if (index === 0) {
          return <td>{item}</td>;
        }
        if (item.isNew) {
          return (
            <td
              onMouseEnter={() => setRuleDeleteIndex(index)}
              onMouseLeave={() => setRuleDeleteIndex(0)}
            >
              <Placeholder onClick={() => sectionPlaceholderClick(false, item, 'isNew')}>
                {t(
                  BUSINESS_LOGIC_TAB.BL_SECTION_TEXT.key,
                  BUSINESS_LOGIC_TAB.BL_SECTION_TEXT.value,
                )}
              </Placeholder>
            </td>
          );
        }
        return (
          <td
            onMouseEnter={() => setRuleDeleteIndex(index)}
            onMouseLeave={() => setRuleDeleteIndex(0)}
          >
            <StyledSelect
              id={`${item.id}-rule-section-name-select`}
              // eslint-disable-next-line eqeqeq
              value={allBlocks.find(block => block.backendId == item.blockId)?.blockName}
              showSearch
              showArrow={false}
              dropdownMatchSelectWidth={false}
              placeholder={t(
                BUSINESS_LOGIC_TAB.BL_SECTION_PLACEHOLDER.key,
                BUSINESS_LOGIC_TAB.BL_SECTION_PLACEHOLDER.value,
              )}
              style={{ minWidth: !item.blockId ? '150px' : '' }}
              onChange={value => {
                updateRuleByKey(value, item);
                // eslint-disable-next-line eqeqeq
                const block = allBlocks.find(block => block.backendId == value);
                updateRuleByKey(!!block.isStandalone, item, 'isStandalone');
              }}
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {diffBlocks.map(block => (
                <Option value={block.backendId}>{block.blockName}</Option>
              ))}
            </StyledSelect>
          </td>
        );
      })}
    </tr>
  );
};

export const TableResultBody = ({ tableBodyResult = [], setRuleDeleteIndex }) => {
  return (
    <tr>
      {tableBodyResult.map((item, index) => {
        return (
          <td
            onMouseEnter={() => setRuleDeleteIndex(index)}
            onMouseLeave={() => setRuleDeleteIndex(0)}
          >
            {item}
          </td>
        );
      })}
    </tr>
  );
};
